import {
  CaretLeftOutlined,
  CaretRightOutlined,
  DesktopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Alert, Button, Modal, Space, Spin, Tag, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { wsUrl } from '../lib/config';
import { useWebSocket } from '../lib/ws';

const { Text } = Typography;

type AppEntry = {
  id: string;
  name: string;
  primary: boolean;
  dominant: boolean;
  slideNumber: number | null;
  totalSlides: number | null;
};

type IncomingMessage =
  | { type: 'app-status'; connected: boolean }
  | {
      type: 'app-list';
      apps: AppEntry[];
      conflict: boolean;
      adminCount: number;
      controllerCount: number;
    }
  | { type: 'state'; enabled: boolean }
  | { type: 'notes'; text: string; slideNumber: number; totalSlides: number };

type UsageEvent = { id: string; title: string; status: 'draft' | 'live' | 'ended' };

/**
 * Slide control test panel — exercises the Slide Control app connected to a
 * license without needing a real speaker session. Renders warnings
 * for both fronts a producer needs to know about:
 *   - Live events currently using this license (so test clicks don't
 *     advance a real broadcast slide).
 *   - Other admins / controllers connected to the same room (so two
 *     producers aren't testing simultaneously and confusing the op).
 */
export function SlideTestPanel({
  open,
  license,
  onClose,
}: {
  open: boolean;
  license: { id: string; name: string; key: string } | null;
  onClose: () => void;
}) {
  const { session } = useAuth();
  const [apps, setApps] = useState<AppEntry[]>([]);
  const [adminCount, setAdminCount] = useState(0);
  const [controllerCount, setControllerCount] = useState(0);
  const [notes, setNotes] = useState<string>('');
  const [slide, setSlide] = useState<{ n: number; t: number } | null>(null);
  const [usage, setUsage] = useState<UsageEvent[] | null>(null);
  const [legacyConnected, setLegacyConnected] = useState(false);

  // Reset state when the panel opens against a new license so a
  // previous test session's stale data doesn't blink in.
  useEffect(() => {
    if (!open) return;
    setApps([]);
    setAdminCount(0);
    setControllerCount(0);
    setNotes('');
    setSlide(null);
    setUsage(null);
    setLegacyConnected(false);
  }, [open, license?.id]);

  // Fetch live-event usage. Re-fetch every 15s while panel is open in
  // case an event starts mid-test.
  useEffect(() => {
    if (!open || !license) return;
    let cancelled = false;
    const fetchUsage = async () => {
      try {
        const res = await api<{ events: UsageEvent[] }>(
          `/api/slide-licenses/${license.id}/usage`,
        );
        if (!cancelled) setUsage(res.events);
      } catch {
        if (!cancelled) setUsage([]);
      }
    };
    void fetchUsage();
    const t = window.setInterval(fetchUsage, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [open, license]);

  const url = useMemo(() => {
    if (!open || !license || !session) return null;
    return wsUrl(
      `/api/slides/${encodeURIComponent(license.key)}?role=admin&token=${encodeURIComponent(session.token)}`,
    );
  }, [open, license, session]);

  const { send } = useWebSocket<IncomingMessage>(url, (msg) => {
    if (msg.type === 'notes') {
      setNotes((msg.text ?? '').replace(/\r/g, '\n'));
      setSlide({ n: msg.slideNumber, t: msg.totalSlides });
    } else if (msg.type === 'app-list') {
      setApps(msg.apps);
      setAdminCount(msg.adminCount);
      setControllerCount(msg.controllerCount);
    } else if (msg.type === 'app-status') {
      setLegacyConnected(msg.connected);
    }
  });

  const liveEvents = (usage ?? []).filter((e) => e.status === 'live');
  const dominantApp = apps.find((a) => a.dominant);
  const anyAppConnected = apps.length > 0 || legacyConnected;
  // Subtract 1 for ourselves — we're an admin too.
  const otherAdmins = Math.max(0, adminCount - 1);

  return (
    <Modal
      title={`Test slide control${license ? ` — ${license.name}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={
        <Button onClick={onClose} type="primary">
          Done
        </Button>
      }
      width={560}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {liveEvents.length > 0 && (
          <Alert
            type="error"
            showIcon
            icon={<WarningOutlined />}
            message={
              liveEvents.length === 1
                ? `"${liveEvents[0]!.title}" is LIVE on this license.`
                : `${liveEvents.length} live events are using this license.`
            }
            description="Clicking Test Next/Prev WILL advance the actual broadcast slide. Only proceed if the producer in the gallery is expecting it."
          />
        )}

        {usage !== null &&
          liveEvents.length === 0 &&
          usage.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`This license is scheduled on ${usage.length} draft event${usage.length === 1 ? '' : 's'}.`}
              description="No live broadcast right now — testing is safe. Listed in case you didn't realise this license is in use elsewhere."
            />
          )}

        {otherAdmins > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${otherAdmins} other admin${otherAdmins === 1 ? ' is' : 's are'} also viewing this license.`}
            description="They'll see your Next/Prev clicks land too. Coordinate before testing so you don't fight each other."
          />
        )}

        {controllerCount > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${controllerCount} speaker${controllerCount === 1 ? ' is' : 's are'} currently connected as the controller.`}
            description="They'll see slide changes from your test in their stage view."
          />
        )}

        <div
          style={{
            border: '1px solid #e5eaf0',
            borderRadius: 6,
            padding: 12,
          }}
        >
          <Space size={8} wrap>
            <DesktopOutlined style={{ color: anyAppConnected ? '#428e3e' : '#999' }} />
            <Text strong>Slide Control app:</Text>
            {anyAppConnected ? (
              <Tag color="green">Connected</Tag>
            ) : (
              <Tag>Not connected</Tag>
            )}
            {slide && (
              <Tag color="blue">
                Slide {slide.n} / {slide.t}
              </Tag>
            )}
            {dominantApp && <Text type="secondary">— {dominantApp.name}</Text>}
          </Space>
        </div>

        {!anyAppConnected && (
          <Spin tip="Waiting for the Slide Control app to connect with this license key…">
            <div style={{ minHeight: 40 }} />
          </Spin>
        )}

        <Space>
          <Button
            size="large"
            icon={<CaretLeftOutlined />}
            onClick={() => send({ type: 'test-prev' })}
            disabled={!anyAppConnected}
          >
            Test Prev
          </Button>
          <Button
            size="large"
            type="primary"
            icon={<CaretRightOutlined />}
            iconPosition="end"
            onClick={() => send({ type: 'test-next' })}
            disabled={!anyAppConnected}
          >
            Test Next
          </Button>
        </Space>

        {notes && (
          <div
            style={{
              background: '#1f1f1f',
              color: '#fff',
              padding: 12,
              borderRadius: 6,
              maxHeight: 200,
              overflowY: 'auto',
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <Text style={{ color: '#bbb', fontSize: 11 }}>
              NOTES (from the slides machine)
            </Text>
            <div
              style={{ marginTop: 6, color: '#fff' }}
              dangerouslySetInnerHTML={{ __html: notes }}
            />
          </div>
        )}
      </Space>
    </Modal>
  );
}
